export interface DataNode {
  id: string;
  name: string;
  value: number;
  prevValue: number;
  change: number;
  level: "campaign" | "adgroup" | "keyword";
  children?: DataNode[];
  keywordMatchType?: string;
}

// Type for the internal processing node, helps manage unnamedAdGroupCounter temporarily
type ProcessingNode = Omit<DataNode, "children" | "level"> & {
  level: "campaign" | "adgroup" | "keyword"; // Level is known during processing
  children: ProcessingNode[] | DataNode[]; // children can be either type during build-up
  unnamedAdGroupCounter?: number; // Only for campaign level processing nodes
};

export function generateHierarchicalDataForMetric(
  currentPeriodKeywordRows: any[],
  previousPeriodKeywordRows: any[],
  metricKey: string,
): { nodes: DataNode[]; accountTotal: number } {
  const campaigns = new Map<string, ProcessingNode>();

  // 1. Create a map of previous period keyword metrics for easy lookup
  const prevKeywordMetricsMap = new Map<string, { value: number }>();
  previousPeriodKeywordRows.forEach((row) => {
    const campaignName = (row.campaign || "").trim();
    const adGroupName = (row.adGroup || "").trim();
    const keywordText = (row.keywordText || "").trim();
    const keywordMatchType = (row.keywordMatchType || "NONE").trim();
    // Key must be consistent with how it's looked up later
    const key =
      `${campaignName}|${adGroupName}|${keywordText}|${keywordMatchType}`.toLowerCase();
    prevKeywordMetricsMap.set(key, { value: Math.max(row[metricKey] || 0, 0) });
  });

  // 2. Process current period rows
  currentPeriodKeywordRows.forEach((row) => {
    const campaignName = (row.campaign || "").trim();
    if (!campaignName) return; // Skip rows with no campaign name

    let rawAdGroupName = row.adGroup || "";
    const keywordText = (row.keywordText || "").trim();
    const keywordMatchType = (row.keywordMatchType || "NONE").trim();
    const metricValue = Math.max(row[metricKey] || 0, 0);

    if (!campaigns.has(campaignName)) {
      campaigns.set(campaignName, {
        id: campaignName,
        name: campaignName,
        value: 0,
        prevValue: 0,
        change: 0,
        level: "campaign",
        children: [],
        unnamedAdGroupCounter: 0,
      });
    }
    const campaignNode = campaigns.get(campaignName)!;
    campaignNode.value += metricValue;

    let adGroupNameForDisplay = rawAdGroupName.trim();
    let adGroupIdSuffix = adGroupNameForDisplay;
    if (adGroupNameForDisplay === "") {
      adGroupNameForDisplay = `(Unnamed Ad Group ${campaignNode.unnamedAdGroupCounter})`;
      adGroupIdSuffix = `__unnamed_${campaignNode.unnamedAdGroupCounter}`;
      // Ensure counter increments only if this specific unnamed ad group ID is new for this campaign
      if (
        !(campaignNode.children as ProcessingNode[]).some(
          (ag) => ag.id === `${campaignName}|${adGroupIdSuffix}`,
        )
      ) {
        if (campaignNode.unnamedAdGroupCounter !== undefined)
          campaignNode.unnamedAdGroupCounter++;
      }
    }
    const agId = `${campaignName}|${adGroupIdSuffix}`;

    let adGroupNode = (campaignNode.children as ProcessingNode[]).find(
      (ag) => ag.id === agId,
    );
    if (!adGroupNode) {
      adGroupNode = {
        id: agId,
        name: adGroupNameForDisplay,
        value: 0,
        prevValue: 0,
        change: 0,
        level: "adgroup",
        children: [],
      };
      (campaignNode.children as ProcessingNode[]).push(adGroupNode);
    }
    adGroupNode.value += metricValue;

    // Link with previous period data for this keyword
    const lookupKeyForPrevMap =
      `${campaignName}|${rawAdGroupName.trim()}|${keywordText}|${keywordMatchType}`.toLowerCase();
    const prevKeywordData = prevKeywordMetricsMap.get(lookupKeyForPrevMap);
    const prevKeywordValue = prevKeywordData?.value || 0;

    let keywordChangePercent = 0;
    if (prevKeywordValue > 0) {
      keywordChangePercent = Math.round(
        ((metricValue - prevKeywordValue) / prevKeywordValue) * 100,
      );
    } else if (metricValue > 0) {
      keywordChangePercent = 100; // Current has value, previous was zero or N/A
    }

    if (metricValue > 0 || prevKeywordValue > 0) {
      // Only add keyword if it has some data
      (adGroupNode.children as DataNode[]).push({
        id: `${agId}|${keywordText}|${keywordMatchType}`,
        name: keywordText,
        value: metricValue,
        prevValue: prevKeywordValue,
        change: keywordChangePercent,
        level: "keyword",
        keywordMatchType: keywordMatchType,
      });
    }
  });

  // 3. Process previous period rows to add entities/data not covered by current period
  previousPeriodKeywordRows.forEach((prevRow) => {
    const campaignName = (prevRow.campaign || "").trim();
    if (!campaignName) return;

    const rawAdGroupName = prevRow.adGroup || "";
    const keywordText = (prevRow.keywordText || "").trim();
    const keywordMatchType = (prevRow.keywordMatchType || "NONE").trim();
    const prevMetricActualValue = Math.max(prevRow[metricKey] || 0, 0);

    if (prevMetricActualValue <= 0) return; // Only care about prev data if it has value

    let campaignNode = campaigns.get(campaignName);
    if (!campaignNode) {
      // Campaign only existed in previous period
      campaignNode = {
        id: campaignName,
        name: campaignName,
        value: 0,
        prevValue: 0,
        change: 0,
        level: "campaign",
        children: [],
        unnamedAdGroupCounter: 0,
      };
      campaigns.set(campaignName, campaignNode);
    }
    campaignNode.prevValue += prevMetricActualValue;

    let adGroupNameForDisplay = rawAdGroupName.trim();
    let adGroupIdSuffix = adGroupNameForDisplay;
    if (adGroupNameForDisplay === "") {
      adGroupNameForDisplay = `(Unnamed Ad Group ${campaignNode.unnamedAdGroupCounter})`;
      adGroupIdSuffix = `__unnamed_${campaignNode.unnamedAdGroupCounter}`;
      if (
        !(campaignNode.children as ProcessingNode[]).some(
          (ag) => ag.id === `${campaignName}|${adGroupIdSuffix}`,
        )
      ) {
        if (campaignNode.unnamedAdGroupCounter !== undefined)
          campaignNode.unnamedAdGroupCounter++;
      }
    }
    const agId = `${campaignName}|${adGroupIdSuffix}`;

    let adGroupNode = (campaignNode.children as ProcessingNode[]).find(
      (ag) => ag.id === agId,
    );
    if (!adGroupNode) {
      // Ad group only existed in previous period for this campaign
      adGroupNode = {
        id: agId,
        name: adGroupNameForDisplay,
        value: 0,
        prevValue: 0,
        change: 0,
        level: "adgroup",
        children: [],
      };
      (campaignNode.children as ProcessingNode[]).push(adGroupNode);
    }
    adGroupNode.prevValue += prevMetricActualValue;

    const keywordFullId = `${agId}|${keywordText}|${keywordMatchType}`;
    const keywordExists = (adGroupNode.children as DataNode[]).some(
      (kw) => kw.id === keywordFullId,
    );

    if (!keywordExists) {
      // Keyword only existed in previous period for this ad group
      (adGroupNode.children as DataNode[]).push({
        id: keywordFullId,
        name: keywordText,
        value: 0, // No current value
        prevValue: prevMetricActualValue,
        change: -100, // Current is 0, previous had value
        level: "keyword",
        keywordMatchType: keywordMatchType,
      });
    }
    // If keyword already exists (added from current period data), its prevValue was already set.
    // This loop primarily ensures that entities (and their prevValues) that *only* existed previously are captured.
  });

  // 4. Calculate changes and sort children
  let overallAccountTotal = 0;
  campaigns.forEach((campaignNode) => {
    overallAccountTotal += campaignNode.value; // Sum up current values for account total

    if (campaignNode.prevValue > 0) {
      campaignNode.change = Math.round(
        ((campaignNode.value - campaignNode.prevValue) /
          campaignNode.prevValue) *
          100,
      );
    } else if (campaignNode.value > 0) {
      campaignNode.change = 100;
    } else {
      campaignNode.change = 0;
    }

    (campaignNode.children as ProcessingNode[]).forEach((adGroupNode) => {
      if (adGroupNode.prevValue > 0) {
        adGroupNode.change = Math.round(
          ((adGroupNode.value - adGroupNode.prevValue) /
            adGroupNode.prevValue) *
            100,
        );
      } else if (adGroupNode.value > 0) {
        adGroupNode.change = 100;
      } else {
        adGroupNode.change = 0;
      }
      (adGroupNode.children as DataNode[]).sort((a, b) => b.value - a.value); // Sort keywords by current value
    });
    (campaignNode.children as DataNode[]).sort((a, b) => b.value - a.value); // Sort ad groups by current value
    delete campaignNode.unnamedAdGroupCounter; // Clean up temporary counter
  });

  // 5. Filter out empty campaigns and sort final list
  const finalNodes: DataNode[] = Array.from(campaigns.values())
    .filter((cn) => cn.value > 0 || cn.prevValue > 0) // Keep if has current or prev value
    .sort((a, b) => b.value - a.value) as DataNode[]; // Sort campaigns by current value

  return { nodes: finalNodes, accountTotal: overallAccountTotal };
}
